import { isEmpty } from 'lodash';

const SUPPORTED_TRIGGER_FIELDS = ['select', 'multiple-choice', 'checkbox'];

function parseBoolean(value) {
  if (value === true || value === false) return value;

  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;

    return false;
  }

  return false;
}

function compareFieldValues(comparison, conditionalValue, logicValue, fieldType) {
  if (fieldType === 'checkbox') {
    switch (comparison) {
      case 'IS':
        return parseBoolean(conditionalValue) === parseBoolean(logicValue);
      case 'IS_NOT':
        return parseBoolean(conditionalValue) !== parseBoolean(logicValue);
      default:
        return false;
    }
  }

  switch (comparison) {
    case 'IS':
      return Array.isArray(conditionalValue)
        ? conditionalValue.includes(logicValue)
        : conditionalValue === logicValue;
    case 'IS_NOT':
      return Array.isArray(conditionalValue)
        ? !conditionalValue.includes(logicValue)
        : conditionalValue !== logicValue;
    case 'IS_EMPTY':
      return isEmpty(conditionalValue);
    case 'IS_NOT_EMPTY':
      return !isEmpty(conditionalValue);
    default:
      return false;
  }
}

function getConditionalValue(fieldId, formFields) {
  const field = formFields[fieldId];

  if (!field) {
    return null;
  }

  switch (field.element) {
    case 'checkbox': {
      return parseBoolean(field.value);
    }
    case 'multiple-choice': {
      // find option id by label
      const option = field.options?.find(o => o.label === field.value);
      return option?._id;
    }
    case 'select': {
      // find option by value
      let option = field.options?.find(o => o.value === field.value);

      // if no option found, find option by label
      if (!option) {
        option = field.options?.find(o => o.label === field.value);
      }

      return option?._id;
    }
    default: {
      return field.value;
    }
  }
}

/**
 * Recursively checks if a field should be enabled (shown) based on showHideLogic.
 * @param {Object} field - The field to check.
 * @param {Object} formFields - Current values of all fields in the form { fieldId: value }.
 * @param {Array} pageFields - Array of all field configurations for the page.
 * @param {Set} visited - Set to detect circular dependencies.
 * @returns {Boolean} - True if the field should be shown, false otherwise.
 */
export default function isFieldEnabled(
  field,
  formFields = {},
  pageFields = [],
  visited = new Set(),
) {
  if (!field?.showHideLogic?.enabled) {
    return true;
  }

  const { element, comparison, value: logicValue } = field.showHideLogic;

  const comparisonField = pageFields.find(f => f._id === element?.elementId);

  if (!comparisonField || !SUPPORTED_TRIGGER_FIELDS.includes(comparisonField.element)) {
    return true;
  }

  const fieldId = field?._id;

  if (visited.has(fieldId)) {
    return false;
  }

  visited.add(fieldId);

  const isComparisonFieldEnabled = isFieldEnabled(comparisonField, formFields, pageFields, visited);

  if (!isComparisonFieldEnabled) {
    visited.delete(fieldId);
    return false;
  }

  const conditionalValue = getConditionalValue(comparisonField?._id, formFields);

  const shouldShow = compareFieldValues(
    comparison,
    conditionalValue,
    logicValue,
    comparisonField.element,
  );

  visited.delete(fieldId);
  return shouldShow;
}
